// src/api/recepcionEquipo.js - API para el módulo de Recepción de Equipos
import { apiFetch } from './index.js';

/**
 * Obtener todas las recepciones de equipos
 * @returns {Promise} Lista de recepciones
 */
export const obtenerRecepciones = async () => {
  try {
    const response = await apiFetch('/RecepcionEquipo');
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data: data,
      count: data.length,
      message: 'Recepciones obtenidas exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo recepciones:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener una recepción por ID
 * @param {number|string} id - ID de la recepción
 * @returns {Promise} Datos de la recepción
 */
export const obtenerRecepcionPorId = async (id) => {
  try {
    if (!id) throw new Error('El ID es requerido');
    const response = await apiFetch(`/RecepcionEquipo/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Recepción obtenida exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo recepción ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener una recepción por ID incluyendo sus relaciones
 * @param {number|string} id - ID de la recepción
 * @returns {Promise} Datos de la recepción con relaciones
 */
export const obtenerRecepcionConRelaciones = async (id) => {
  try {
    if (!id) throw new Error('El ID es requerido');
    const response = await apiFetch(`/RecepcionEquipo/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response,
      message: 'Recepción con relaciones obtenida exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo relaciones de recepción ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * 🔥 Crear una nueva recepción de equipo
 * ✅ EL NÚMERO DE ORDEN SE GENERA AUTOMÁTICAMENTE EN EL BACKEND
 * @param {Object} data - Datos de la recepción y sus detalles
 * @returns {Promise} Resultado de la creación
 */
export const crearRecepcion = async (data) => {
  try {
    // ✅ Validar campos obligatorios (numeroOrden NO es requerido en frontend)
    if (!data.idCliente) throw new Error('El cliente es requerido');
    if (!data.idTecnicoAsignado) throw new Error('El técnico asignado es requerido');
    
    // ✅ ELIMINAR cualquier rastro de numeroOrden del payload
    const payload = { ...data };
    delete payload.numeroOrden;
    delete payload.NumeroOrden;
    
    // Asegurar que los IDs sean números
    if (payload.idCliente) payload.idCliente = parseInt(payload.idCliente);
    if (payload.idUsuario) payload.idUsuario = parseInt(payload.idUsuario);
    if (payload.idTecnicoAsignado) payload.idTecnicoAsignado = parseInt(payload.idTecnicoAsignado);
    if (payload.idProductoServicios) payload.idProductoServicios = parseInt(payload.idProductoServicios);
    if (payload.idEstadoRecepcion) payload.idEstadoRecepcion = parseInt(payload.idEstadoRecepcion);
    if (payload.idSede) payload.idSede = parseInt(payload.idSede);
    
    console.log('📤 [API] Enviando recepción SIN numeroOrden:', payload);
    
    const response = await apiFetch('/RecepcionEquipo', {
      method: 'POST',
      body: payload
    });
    
    console.log('📥 [API] Recepción creada con número:', response.numeroOrden || response.NumeroOrden);
    
    return { 
      success: true, 
      data: response, 
      message: `Recepción #${response.numeroOrden || response.NumeroOrden || ''} registrada exitosamente` 
    };
  } catch (error) {
    console.error('❌ Error creando recepción:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Actualizar una recepción existente
 * @param {number|string} id - ID de la recepción
 * @param {Object} data - Datos a actualizar
 * @returns {Promise} Resultado de la actualización
 */
export const actualizarRecepcion = async (id, data) => {
  try {
    if (!id) throw new Error('El ID es requerido');
    
    const response = await apiFetch(`/RecepcionEquipo/${id}`, {
      method: 'PUT',
      body: { 
        ...data, 
        idRecepcionEquipo: parseInt(id) 
      }
    });
    
    return { 
      success: true, 
      data: response, 
      message: 'Recepción de equipo actualizada exitosamente' 
    };
  } catch (error) {
    console.error(`❌ Error actualizando recepción ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Eliminar una recepción
 * @param {number|string} id - ID de la recepción a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarRecepcion = async (id) => {
  try {
    if (!id) throw new Error('El ID es requerido');
    await apiFetch(`/RecepcionEquipo/${id}`, { method: 'DELETE' });
    
    return { success: true, message: 'Recepción eliminada exitosamente' };
  } catch (error) {
    console.error(`❌ Error eliminando recepción ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar recepciones con filtros avanzados
 * @param {Object} filtros - Criterios de búsqueda
 * @returns {Promise} Resultados de la búsqueda
 */
export const buscarRecepciones = async (filtros) => {
  try {
    const response = await apiFetch('/RecepcionEquipo/search', {
      method: 'POST',
      body: filtros
    });
    
    return { 
      success: true, 
      data: response,
      message: 'Búsqueda completada'
    };
  } catch (error) {
    console.error('❌ Error buscando recepciones:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Cambiar solo el estado de una recepción (PATCH)
 * @param {number|string} id - ID de la recepción
 * @param {number} nuevoEstadoId - ID del nuevo estado
 * @param {string} observacion - Observación opcional
 * @returns {Promise}
 */
export const cambiarEstadoSimple = async (id, nuevoEstadoId, observacion = '') => {
  try {
    const usuarioId = parseInt(localStorage.getItem('idUsuario') || 1);
    const url = `/RecepcionEquipo/${id}/estado?nuevoEstadoId=${nuevoEstadoId}&usuarioId=${usuarioId}&observacion=${encodeURIComponent(observacion)}`;
    
    await apiFetch(url, { method: 'PATCH' });
    
    return { success: true, message: 'Estado actualizado correctamente' };
  } catch (error) {
    console.error('❌ Error en cambiarEstadoSimple:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Actualizar el estado de una recepción
 * @param {number|string} id - ID de la recepción
 * @param {number} idEstado - Nuevo ID de estado
 * @returns {Promise} Resultado de la actualización
 */
export const actualizarEstadoRecepcion = async (id, idEstado, statusName, data = {}) => {
  try {
    if (!id) throw new Error('El ID es requerido');
    
    const payload = {
      precioUnitarioUsd: parseFloat(data.precioUnitarioUsd || 0),
      subtotalUsd: parseFloat(data.subtotalUsd || 0),
      precioUnitarioBs: parseFloat(data.precioUnitarioBs || 0),
      subtotalBs: parseFloat(data.subtotalBs || 0),
      costoUnitarioUsd: parseFloat(data.costoUnitarioUsd || 0),
      costoTotalUsd: parseFloat(data.costoTotalUsd || 0),
      comisionTecnicoUsd: parseFloat(data.comisionTecnicoUsd || 0),
      garantiaDias: parseInt(data.garantiaDias || 0),
      idServicio: data.idServicio && data.idServicio !== 0 ? parseInt(data.idServicio) : null,
      idProducto: data.idProducto && data.idProducto !== 0 ? parseInt(data.idProducto) : null,
      idTecnico: data.idTecnico && data.idTecnico !== 0 ? parseInt(data.idTecnico) : null,
      idEstadoRecepcion: parseInt(idEstado),
      actividadesRealizadas: data.actividadesRealizadas || ''
    };

    console.log(`📝 Enviando actualización de estado para ID ${id}:`, payload);

    // 1. ACTUALIZAR ESTADO PADRE
    try {
      await apiFetch(`/RecepcionEquipo/${id}/estado?nuevoEstadoId=${idEstado}&usuarioId=${data.idTecnico || 0}&observacion=${encodeURIComponent(statusName)}`, {
        method: 'PATCH'
      });
    } catch(e) {
      console.warn('⚠️ Nota: El endpoint PATCH /estado regresó un error:', e);
    }

    // 2. CREAR DETALLE Y COSTOS
    const response = await apiFetch(`/RecepcionEquipo/${id}/detalle`, {
      method: 'POST',
      body: payload
    });
    
    return { 
      success: true, 
      data: response, 
      message: `Estado actualizado a ${statusName} correctamente` 
    };
  } catch (error) {
    console.error(`❌ Error actualizando estado de recepción ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener recepciones filtradas por cliente
 * @param {number} idCliente - ID del cliente
 * @returns {Promise} Lista de recepciones
 */
export const obtenerRecepcionesPorCliente = async (idCliente) => {
  try {
    if (!idCliente) throw new Error('El ID del cliente es requerido');
    const response = await apiFetch(`/RecepcionEquipo/cliente/${idCliente}`);
    
    return { 
      success: true, 
      data: response,
      count: Array.isArray(response) ? response.length : 0
    };
  } catch (error) {
    console.error(`❌ Error obteniendo recepciones del cliente ${idCliente}:`, error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Obtener recepciones filtradas por técnico
 * @param {number} idTecnico - ID del técnico
 * @returns {Promise} Lista de recepciones
 */
export const obtenerRecepcionesPorTecnico = async (idTecnico) => {
  try {
    if (!idTecnico) throw new Error('El ID del técnico es requerido');
    const response = await apiFetch(`/RecepcionEquipo/tecnico/${idTecnico}`);
    
    return { 
      success: true, 
      data: response,
      count: Array.isArray(response) ? response.length : 0
    };
  } catch (error) {
    console.error(`❌ Error obteniendo recepciones del técnico ${idTecnico}:`, error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Obtener recepciones filtradas por estado
 * @param {number} idEstado - ID del estado
 * @returns {Promise} Lista de recepciones
 */
export const obtenerRecepcionesPorEstado = async (idEstado) => {
  try {
    if (!idEstado) throw new Error('El ID del estado es requerido');
    const response = await apiFetch(`/RecepcionEquipo/estado/${idEstado}`);
    
    return { 
      success: true, 
      data: response,
      count: Array.isArray(response) ? response.length : 0
    };
  } catch (error) {
    console.error(`❌ Error obteniendo recepciones por estado ${idEstado}:`, error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Verificar si un número de orden ya existe en el sistema
 * @param {string} numeroOrden - Número de orden a verificar
 * @returns {Promise} Objeto con propiedad 'existe'
 */
export const verificarNumeroOrdenExistente = async (numeroOrden) => {
  try {
    if (!numeroOrden) return { success: true, existe: false };
    
    const response = await apiFetch(`/RecepcionEquipo/numeroOrden/${encodeURIComponent(numeroOrden)}/exists`);
    
    return { 
      success: true, 
      existe: response.exists || response.data?.exists || false,
      message: response.exists ? 'El número de orden ya está registrado' : 'Número de orden disponible'
    };
  } catch (error) {
    console.error(`❌ Error verificando número de orden ${numeroOrden}:`, error);
    return { success: false, existe: false, error: error.message };
  }
};

/**
 * Agregar un detalle a una recepción
 * @param {number|string} id - ID de la recepción
 * @param {Object} detalle - Datos del detalle
 * @returns {Promise} Resultado de la creación
 */
export const crearDetalleRecepcion = async (id, detalle) => {
  try {
    if (!id) throw new Error('El ID de recepción es requerido');
    const response = await apiFetch(`/RecepcionEquipo/${id}/detalle`, {
      method: 'POST',
      body: detalle
    });
    
    return { 
      success: true, 
      data: response, 
      message: 'Detalle agregado exitosamente' 
    };
  } catch (error) {
    console.error(`❌ Error creando detalle para recepción ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * 🔥 Obtener el último número de la secuencia (para mostrar en UI)
 * ✅ Este endpoint devuelve el número ACTUAL de la secuencia
 * @returns {Promise} El último número (ej: 6688)
 */
export const obtenerUltimoNumeroOrden = async () => {
  try {
    const response = await apiFetch('/RecepcionEquipo/ultimoNumero');
    return { success: true, data: response };
  } catch (error) {
    console.error('❌ Error obteniendo último número de orden:', error);
    return { success: false, error: error.message, data: 6000 };
  }
};

/**
 * 🔥 Sincronizar la secuencia con el máximo existente en BD
 * @returns {Promise} Resultado de la sincronización
 */
export const sincronizarSecuencia = async () => {
  try {
    const response = await apiFetch('/RecepcionEquipo/sincronizar-secuencia', {
      method: 'POST'
    });
    return { success: true, data: response };
  } catch (error) {
    console.error('❌ Error sincronizando secuencia:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Registra la entrega final de un equipo y procesa su pago
 * @param {number|string} idRecepcion - ID de la recepción
 * @param {Object} payload - Objeto con datos del cajero, montos y pago
 * @returns {Promise}
 */
export const registrarEntregaPago = async (idRecepcion, payload) => {
  try {
    if (!idRecepcion) throw new Error('El ID de recepción es requerido para pagar.');

    let idDetalleOriginal = null;
    let respDetalles = null;
    try {
      respDetalles = await apiFetch(`/RecepcionEquipo/${idRecepcion}/conRelaciones`);
      const listaDetalles = respDetalles.detalles || respDetalles.Detalles || respDetalles.recepcionEquipoDetalles || [];
      if (listaDetalles && listaDetalles.length > 0) {
         idDetalleOriginal = listaDetalles[0].idRecepcionEquipoDetalle || listaDetalles[0].IdRecepcionEquipoDetalle;
      }
    } catch(e) { 
      console.warn("No se pudo extraer detalle original", e); 
      if (e.message && e.message.includes('404')) {
        throw new Error(`La recepción con ID ${idRecepcion} ya no existe en la base de datos.`);
      }
    }

    if(!idDetalleOriginal) {
      console.log("Creando detalle técnico base para sustentar salida...");
      try {
        const resDetalle = await apiFetch(`/RecepcionEquipo/${idRecepcion}/detalle`, { 
          method: 'POST', 
          body: {
            cantidad: 1,
            precioUnitarioUsd: 0,
            subtotalUsd: 0,
            precioUnitarioBs: 0,
            subtotalBs: 0,
            idTecnico: payload.IdUsuarioEntrega,
            idEstadoRecepcion: payload.idEstadoSalida || 1
          }
        });
        if(resDetalle && (resDetalle.idRecepcionEquipoDetalle || resDetalle.IdRecepcionEquipoDetalle)) {
          idDetalleOriginal = resDetalle.idRecepcionEquipoDetalle || resDetalle.IdRecepcionEquipoDetalle;
        }
      } catch (e) {
        if (e.message && e.message.includes('404')) {
          throw new Error(`La recepción con ID ${idRecepcion} ya no existe en la base de datos.`);
        }
        throw e;
      }
    }

    if (!idDetalleOriginal) throw new Error("La orden no tiene un detalle técnico válido para procesar una salida.");

    const SalidaPayload = {
      PrecioUnitarioUsd: payload.TotalOriginal || payload.MontoUsd,
      SubtotalUsd: payload.TotalOriginal || payload.MontoUsd,
      PrecioUnitarioBs: (payload.TotalOriginal || payload.MontoUsd) * (payload.TasaVenta || 1),
      SubtotalBs: (payload.TotalOriginal || payload.MontoUsd) * (payload.TasaVenta || 1),
      IdRecepcionEquipoDetalle: parseInt(idDetalleOriginal),
      IdUsuario: payload.IdUsuarioEntrega,
      IdEstadoRecepcion: payload.idEstadoSalida || 3 
    };

    const salidaRes = await apiFetch('/SalidaEquipoDetalle', {
      method: 'POST',
      body: SalidaPayload
    });

    const idSalidaEquipoDetalle = salidaRes.idSalidaEquipoDetalle || salidaRes.IdSalidaEquipoDetalle;

    if (!salidaRes || !idSalidaEquipoDetalle) {
      throw new Error("No se pudo generar el registro de salida del equipo.");
    }

    const PagoPayload = {
      IdSalidaEquipoDetalle: idSalidaEquipoDetalle,
      NumeroRecibo: payload.NumeroRecibo || `REC-${Date.now()}`,
      FechaPago: payload.FechaPago || new Date().toISOString(),
      MontoUsd: payload.MontoUsd,
      MontoBs: payload.MontoBs,
      TasaBsUsd: payload.TasaVenta || 1,
      IdFuenteTasa: payload.IdFuenteTasa || 1,
      IdTasaVenta: payload.IdTasaVenta || 1,
      TipoPago: payload.MontoUsd >= payload.TotalOriginal ? "Completo" : "Abono",
      SaldoPendienteUsd: Math.max(0, (payload.TotalOriginal) - (payload.MontoUsd || 0)),
      MetodoPago: payload.MetodoPago,
      Referencia: payload.Referencia || "S/R",
      IdUsuario: payload.IdUsuarioCajero || payload.IdUsuarioEntrega || 1,
      Estado: "Completado",
      Observaciones: payload.Observaciones || "Pago procesado desde Seguimiento de Orden."
    };

    const pagoRes = await apiFetch('/PagoSalidaEquipo', {
      method: 'POST',
      body: PagoPayload
    });

    try {
      if (respDetalles) {
        const currentEstado = respDetalles.idEstadoRecepcion || respDetalles.IdEstadoRecepcion;
        const targetEstado = (payload.idEstadoSalida && payload.idEstadoSalida !== 0) ? payload.idEstadoSalida : (currentEstado || 4);

        const updateData = { ...respDetalles };
        
        const propsToDelete = [
          'recepcionEquipoDetalles', 'cliente', 'tecnicoReparador', 
          'tecnicoEntrega', 'tecnicoAsignado', 'estadoRecepcion',
          'usuario', 'usuarioEntrega', 'usuarioCajero'
        ];
        propsToDelete.forEach(p => {
          delete updateData[p];
          const pascalP = p.charAt(0).toUpperCase() + p.slice(1);
          delete updateData[pascalP];
        });

        const finalBody = {
          IdRecepcionEquipo: parseInt(idRecepcion),
          NumeroOrden: respDetalles.numeroOrden || respDetalles.NumeroOrden || '',
          IdCliente: respDetalles.idCliente || respDetalles.IdCliente,
          IdUsuario: respDetalles.idUsuario || respDetalles.IdUsuario,
          IdEstadoRecepcion: targetEstado,
          IdTecnicoAsignado: respDetalles.idTecnicoAsignado || respDetalles.IdTecnicoAsignado,
          IdTecnicoRecepcion: respDetalles.idTecnicoRecepcion || respDetalles.IdTecnicoRecepcion,
          IdTecnicoReparador: respDetalles.idTecnicoReparador || respDetalles.IdTecnicoReparador,
          IdTecnicoEntrega: payload.IdUsuarioEntrega,
          PrecioTotalUsd: respDetalles.precioTotalUsd || respDetalles.PrecioTotalUsd || 0,
          Problema: respDetalles.problema || respDetalles.Problema || 'N/A',
          Descripcion: respDetalles.descripcion || respDetalles.Descripcion || 'N/A',
          Accesorios: respDetalles.accesorios || respDetalles.Accesorios || 'Ninguno',
          ActividadesRealizadas: `${respDetalles.actividadesRealizadas || respDetalles.ActividadesRealizadas || ''}\n\nEntrega Orden #${idRecepcion}.`,
          Repuestos: respDetalles.repuestos || respDetalles.Repuestos || '',
          ClavePatron: respDetalles.clavePatron || respDetalles.ClavePatron || '',
          Taller: respDetalles.taller || respDetalles.Taller || false,
          TipoDiagnostico: respDetalles.tipoDiagnostico || respDetalles.TipoDiagnostico || 'GRATIS',
          MontoDiagnostico: respDetalles.montoDiagnostico || respDetalles.MontoDiagnostico || 0,
          FechaIngreso: respDetalles.fechaIngreso || respDetalles.FechaIngreso,
          FechaEntrega: payload.FechaEntrega || new Date().toISOString(),
          FechaPago: payload.FechaPago || new Date().toISOString(),
          IdSede: respDetalles.idSede || respDetalles.IdSede || 1,
          IdProductoServicios: respDetalles.idProductoServicios || respDetalles.IdProductoServicios
        };

        console.log("🛠️ Sincronizando orden técnica:", finalBody);
        
        await apiFetch(`/RecepcionEquipo/${idRecepcion}`, {
          method: 'PUT',
          body: finalBody
        });
      } else {
        console.warn("⚠️ No se pudo obtener la orden completa, intentando actualización parcial de estado.");
        await apiFetch(`/RecepcionEquipo/${idRecepcion}/estado?nuevoEstadoId=${payload.idEstadoSalida || 4}&usuarioId=${payload.IdUsuarioEntrega}`, {
          method: 'PATCH'
        });
      }
    } catch(e) { console.warn("No se pudo actualizar el técnico de entrega en la orden principal", e); }

    return { 
      success: true, 
      data: {
        pago: pagoRes,
        idSalidaEquipoDetalle: salidaRes.idSalidaEquipoDetalle
      },
      message: 'Cobro y Salida procesados exitosamente.'
    };
  } catch (error) {
    console.error('❌ Error en flujo de pago:', error);
    return { success: false, error: error.message };
  }
};

// ==================== FUNCIONES DE FORMATEO ====================

export const formatearFechaRecepcion = (fechaISO) => {
  if (!fechaISO) return 'N/A';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return fechaISO;
  }
};

export const formatearMonto = (monto, moneda = 'usd') => {
  if (monto === undefined || monto === null) return '0.00';
  const formatter = new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return moneda.toLowerCase() === 'usd' 
    ? `$${formatter.format(monto)}` 
    : `Bs. ${formatter.format(monto)}`;
};

export default {
    obtenerRecepciones,
    obtenerRecepcionPorId,
    obtenerRecepcionConRelaciones,
    crearRecepcion,
    actualizarRecepcion,
    eliminarRecepcion,
    buscarRecepciones,
    actualizarEstadoRecepcion,
    cambiarEstadoSimple,
    obtenerRecepcionesPorCliente,
    obtenerRecepcionesPorTecnico,
    obtenerRecepcionesPorEstado,
    verificarNumeroOrdenExistente,
    crearDetalleRecepcion,
    obtenerUltimoNumeroOrden,
    sincronizarSecuencia,
    registrarEntregaPago,
    formatearFechaRecepcion,
    formatearMonto
};